<template>
  <Layout>
  <div class="px-4 py-8 sm:px-6 md:py-12 lg:px-16 bg-gray-50 min-h-screen">
    <!-- Loading Indicator for Categories -->
    <div v-if="isLoadingCategories" class="text-center py-10">
      <p class="text-lg text-gray-500">Loading categories...</p>
    </div>
    <h1 class="text-3xl sm:text-[40px] font-[900] mb-8 capitalize text-center text-[#00395a]">Photo Gallery</h1>

    <!-- Category Navigation -->
    <div v-if="!isLoadingCategories && categories.length > 0" class="mb-8">
      <div class="flex flex-wrap justify-center gap-3 sm:gap-4">
        <button
          v-for="category in categories"
          :key="category.id"
          @click="selectCategory(category.slug)"
          class="px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ease-in-out focus:outline-none"
          :class="selectedCategorySlug === category.slug ? 'bg-[#00395a] text-white shadow-md' : 'bg-white text-[#00395a] border border-[#00395a] hover:bg-gray-100'"
        >
          {{ category.name }}
        </button>
      </div>
    </div>

    <!-- Gallery Content for Selected Category -->
    <div v-if="isLoadingImages" class="text-center py-10">
      <p class="text-lg text-gray-500">Loading images for {{ selectedCategoryName }}...</p>
    </div>
    <div v-else-if="selectedCategorySlug && !isLoadingImages">
      <h2 class="text-2xl sm:text-3xl font-[800] mb-6 capitalize text-center text-[#00395a]">
        {{ selectedCategoryName }}
      </h2>

      <div v-if="groupedImagesByYear.length > 0">
        <div v-for="group in groupedImagesByYear" :key="group.year" class="mb-10">
          <h3 class="text-xl sm:text-2xl font-[700] mb-6 text-[#00395a] border-b-2 border-[#00395a] pb-2 inline-block">
            {{ group.year }}
          </h3>
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
            <div v-for="imageItem in group.items" :key="imageItem.id" class="relative overflow-hidden rounded-lg shadow-md group cursor-pointer aspect-w-1 aspect-h-1 h-52">
              <img
                :src="imageItem.imageUrl"
                :alt="imageItem.altText || 'Gallery image from ' + imageItem.date"
                class="absolute inset-0 w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-105"
                />
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p class="text-center text-gray-600 text-lg">No images available for {{ selectedCategoryName }}.</p>
      </div>
    </div>
    <div v-else-if="!isLoadingCategories && categories.length === 0" class="text-center py-10">
        <p class="text-center text-gray-600 text-lg">No photo categories available at the moment.</p>
    </div>
    <div v-else-if="!isLoadingCategories && !selectedCategorySlug" class="text-center py-10">
       <p class="text-center text-gray-600 text-lg">Select a category to view the gallery.</p>
    </div>
  </div>
  </Layout>
</template>

<script>
import Layout from './../layouts/LayoutView.vue';
import axiosInstance from '@/utils/axiosInstance'; // Ensure this path is correct
import placeholderImage from './../../assets/placeholder-image.jpeg';

export default {
  name: 'GalleryPage',
  components: {
    Layout
  },
  data() {
    return {
      allCategoriesData: [], // Stores raw category data from Strapi (Gallery-Category)
      selectedCategorySlug: null, // UID (gallerySlug) of the currently selected category
      selectedCategoryImages: [], // Stores raw image data for the selected category (Gallery-Items)
      isLoadingCategories: false,
      isLoadingImages: false,
      placeholderImage: placeholderImage,
    };
  },
  computed: {
    categories() {
      // Maps Strapi category data to a simpler format for buttons
      return this.allCategoriesData.map(category => ({
        id: category.id, // Strapi ID for keys
        slug: category.attributes.gallerySlug,
        name: category.attributes.galleryTag,
      }));
    },
    selectedCategoryName() {
      const selected = this.allCategoriesData.find(cat => cat.attributes.gallerySlug === this.selectedCategorySlug);
      return selected ? selected.attributes.galleryTag : '';
    },
    groupedImagesByYear() {
      if (!this.selectedCategoryImages.length) {
        return [];
      }

      const groups = {};
      this.selectedCategoryImages.forEach(item => {
        // Ensure galleryImageDate is present and valid
        const dateValue = item.attributes.galleryImageDate;
        if (!dateValue) {
          console.warn(`Missing galleryImageDate for item ID ${item.id}`);
          return; // Skip items with no date
        }
        const year = new Date(dateValue).getFullYear();
        if (!groups[year]) {
          groups[year] = [];
        }
        groups[year].push({
          id: item.id, // Strapi ID for Gallery-Item
          imageUrl: this.getStrapiMediaUrl(item.attributes.galleryImage),
          date: dateValue,
          // Use alternativeText if available, otherwise the image name, or a generic alt text
          altText: item.attributes.galleryImage?.data?.attributes?.alternativeText || 
                   item.attributes.galleryImage?.data?.attributes?.name || 
                   `Image from ${dateValue}`
        });
      });

      return Object.keys(groups)
        .map(year => ({
          year: parseInt(year),
          items: groups[year].sort((a, b) => new Date(b.date) - new Date(a.date)), // Sort images by date descending
        }))
        .sort((a, b) => b.year - a.year); // Sort year groups descending
    },
  },
  async created() {
    await this.fetchCategories();
    // Auto-select the first category if categories are loaded and none is selected yet
    if (this.categories.length > 0 && !this.selectedCategorySlug) {
      this.selectCategory(this.categories[0].slug);
    }
  },
  methods: {
    getStrapiMediaUrl(mediaAttribute) {
      if (mediaAttribute && mediaAttribute.data && mediaAttribute.data.attributes && mediaAttribute.data.attributes.url) {
        const url = mediaAttribute.data.attributes.url;
        // If your Strapi server.js `url` is configured (e.g. http://karanmagdani.local:1337)
        // and it serves absolute URLs, this should work.
        // If Strapi returns relative URLs (e.g., "/uploads/..."), you might need to prepend your Strapi base URL.
        // Example: if (url.startsWith('/')) return `http://your-strapi-domain.com${url}`;
        return url;
      }
      return this.placeholderImage; // Fallback to placeholder
    },
    async fetchCategories() {
      this.isLoadingCategories = true;
      try {
        // Use your Strapi collection API ID for categories, e.g., 'gallery-categories'
        // Sort by galleryTag for consistent ordering
        const response = await axiosInstance.get('/gallery-categories?sort=galleryTag:asc');
        this.allCategoriesData = response.data.data || [];
      } catch (error) {
        console.error('Failed to fetch gallery categories:', error);
        this.allCategoriesData = []; // Reset on error
      } finally {
        this.isLoadingCategories = false;
      }
    },
    async fetchImagesForCategory(slug) {
      if (!slug) return;
      this.isLoadingImages = true;
      this.selectedCategoryImages = []; // Clear previous images
      try {
        // Use your Strapi collection API ID for items, e.g., 'gallery-items'
        // Filter by the gallerySlug of the related gallery_category
        // Populate the galleryImage field
        // Sort by galleryImageDate descending
        const response = await axiosInstance.get(
          `/gallery-items?filters[gallery_category][gallerySlug][$eq]=${slug}&populate=galleryImage&sort=galleryImageDate:desc`
        );
        this.selectedCategoryImages = response.data.data || [];
      } catch (error) {
        console.error(`Failed to fetch images for category ${slug}:`, error);
        this.selectedCategoryImages = []; // Reset on error
      } finally {
        this.isLoadingImages = false;
      }
    },
    async selectCategory(slug) {
      this.selectedCategorySlug = slug;
      await this.fetchImagesForCategory(slug);
    },
  },
};
</script>

<style scoped>
/* Custom styles if needed, but Tailwind classes handle most of it */
</style>